import React from "react";
import { Link } from "react-router-dom";

import { faHotel } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import AccountStore from "~/account/AccountStore";
import { getLoginProviderText } from "~/auth/components/AuthButton";
import {
	type AuthPostMessage,
	type OAuthParams,
	promptCustomAuth,
	promptGoogleAuth,
	promptOffice365Auth,
	promptOktaAuth,
	promptOneLoginAuth,
} from "~/lib/auth/index";
import { isExtensionVersionReleased } from "~/lib/browserExtension";
import { dispatchAnalyticsTrack, getReferringEntities } from "~/lib/segment";
import { inVideocallMode, isNative, listFormat } from "~/lib/utils";
import Avatar from "~/ui/legacy/Avatar";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import { AuthBase } from "../AuthBase";
import { AuthButton, SignupHint, Terms } from "../components";
import WhitelabelPane from "../panes/WhitelabelPane";
import type { AuthProps } from "../types";
import { GoogleMeetPreAuth } from "./GoogleMeetPreAuth";
import { WorkspaceRow } from "./utils";

import styles from "../css/Auth.module.css";

export interface LoginState {
	errorCode: string | null;
	errorMessage: string | undefined;
	showSignup: boolean;
	fromEmail: boolean;
	loginRedirect: string | undefined;
	streamInviteId: string | undefined;
	// Used for outlook login
	outlookShowSafariPrompt: boolean;
	isExpanded?: boolean;
	outlookContinueAfterSafariPrompt?: () => void;
}

export default class Login<P extends AuthProps = AuthProps> extends AuthBase<P, LoginState> {
	documentTitle = "Login";

	constructor(props: P) {
		super(props);

		const params = new URLSearchParams(window.location.search);

		this.state = {
			errorCode: null,
			errorMessage: params.get("errorMessage") ?? undefined,
			showSignup: params.has("next") || params.has("login_redirect"),
			fromEmail: params.get("utm_medium") === "email",
			loginRedirect: window.INITIAL_STATE.login_redirect || params.get("next") || undefined,
			streamInviteId: params.get("stream_invite") ?? undefined,
			outlookShowSafariPrompt: false,
			isExpanded: false,
		};
	}

	getProvider() {
		const { provider } = window.INITIAL_STATE;
		return provider;
	}

	getPaneComponent() {
		return WhitelabelPane;
	}

	getPaneProps() {
		return { bgStyle: "purple" as const };
	}

	genericAuthLogin = (
		authPrompt: (params: OAuthParams) => Promise<AuthPostMessage>,
		permissionsDenied: string,
		analyticsTag: string,
	): void => {
		/* eslint-disable camelcase */
		const analyticsData = {
			feature: "Authentication",
			integration: analyticsTag,
			integration_initial_provider: window.INITIAL_STATE.provider,
		};
		/* eslint-enable camelcase */

		this.setState({ errorCode: null, errorMessage: "" });

		const searchParams = new URLSearchParams(window.location.search);
		const redirectBackToDesktopOnLastOauthStep = searchParams.get("redirectBackToDesktopOnLastOauthStep");

		dispatchAnalyticsTrack("Login Step Auth Button Clicked", analyticsData);

		authPrompt({
			accountID: this.context.account?.id,
			redirectBackToDesktopOnLastOauthStep: redirectBackToDesktopOnLastOauthStep === "1" ? true : false,
		})
			.then(data => {
				dispatchAnalyticsTrack("Login Step Completed", analyticsData);

				window.setTimeout(() => {
					if (data.signup?.redirect) {
						window.location.href = data.signup.redirect;
					} else if (data.signup?.next) {
						this.context.setAuthContext(data.signup);
						this.props.history.push(data.signup.next, data);
					} else if (data.next) {
						window.location.replace(data.next);
					} else {
						this.props.history.push("/");
					}
				}, 100);
			})
			// eslint-disable-next-line camelcase
			.catch(({ code, message, email, signed_email }: AuthPostMessage) => {
				if (isNative && code === "no_account_found") {
					this.props.history.push("/auth/captive/");
				}
				if (code === permissionsDenied) {
					dispatchAnalyticsTrack("Login Step Failed", {
						reason: "integration_permission_rejected",
						...analyticsData,
					});
				} else if (code === "mismatched_email") {
					dispatchAnalyticsTrack("Login Step Failed", { reason: "mismatched_email", ...analyticsData });
					// eslint-disable-next-line camelcase
					window.HISTORY.push("/auth/alias/", { oauth_email: email, signed_oauth_email: signed_email });
				} else if (code) {
					dispatchAnalyticsTrack("Login Step Failed", analyticsData);
					this.setState({ errorCode: code, errorMessage: message });
				} else {
					dispatchAnalyticsTrack("Login Step Failed", analyticsData);
					this.setState({ errorCode: null, errorMessage: message });
				}
			});
	};

	loginWithGoogle = (): void => {
		this.genericAuthLogin(promptGoogleAuth, "access_denied", "google");
	};

	loginWithOffice365 = (): void => {
		this.genericAuthLogin(promptOffice365Auth, "consent_required", "office365");
	};

	loginWithOkta = (): void => {
		this.genericAuthLogin(promptOktaAuth, "", "okta");
	};

	loginWithOneLogin = (): void => {
		this.genericAuthLogin(promptOneLoginAuth, "", "one_login");
	};

	loginWithCustomSSO = (): void => {
		this.genericAuthLogin(promptCustomAuth, "", "custom_sso");
	};

	signupWith = (
		authPrompt: (params: OAuthParams) => Promise<AuthPostMessage>,
		permissionsDenied: string,
		analyticsTag: string,
	): void => {
		let params: OAuthParams = {
			signup: "generic",
			accountID: this.context.account?.id,
			referrer: JSON.stringify(getReferringEntities()),
		};

		/* eslint-disable camelcase */
		const analyticsData = {
			feature: "Authentication",
			integration: analyticsTag.toLowerCase(),
			integration_initial_provider: this.getProvider(),
			variant: "auth_prompt",
			...getReferringEntities(),
		};
		/* eslint-enable camelcase */

		this.setState({ errorCode: null, errorMessage: "" });

		dispatchAnalyticsTrack("Signup Auth Button on Login Page Clicked", analyticsData);

		authPrompt(params)
			.then(data => {
				dispatchAnalyticsTrack("Signup from Login Page Completed", analyticsData);
				window.setTimeout(() => {
					if (data.signup?.redirect) {
						window.location.href = data.signup.redirect;
					} else if (data.signup?.next) {
						this.context.setAuthContext(data.signup);
						this.props.history.push(data.signup.next, data);
					} else if (data.next) {
						/* eslint-disable camelcase */
						dispatchAnalyticsTrack("Signed Up", {
							is_account_created: true,
							...analyticsData,
						});
						/* eslint-enable camelcase */

						window.location.href = data.next;
					}
				}, 100);
			})
			// eslint-disable-next-line camelcase
			.catch(({ code, message, email, signed_email }: AuthPostMessage) => {
				if (code === permissionsDenied) {
					dispatchAnalyticsTrack("Signup from Login Page Failed", {
						reason: "integration_permission_rejected",
						...analyticsData,
					});
				} else if (code === "mismatched_email") {
					dispatchAnalyticsTrack("Signup from Login Page Failed", { reason: "mismatched_email", ...analyticsData });
					// eslint-disable-next-line camelcase
					this.props.history.push("/auth/alias/", {
						/* eslint-disable camelcase */
						oauth_email: email,
						signed_oauth_email: signed_email,
						/* eslint-enable camelcase */
						...this.props.location.state,
					});
				} else {
					dispatchAnalyticsTrack("Signup from Login Page Failed", analyticsData);
					this.setState({ errorMessage: message });
				}
			});
	};

	signupWithGoogle = (): void => {
		this.signupWith(promptGoogleAuth, "access_denied", "google");
	};

	signupWithOffice365 = (): void => {
		this.signupWith(promptOffice365Auth, "consent_required", "office365");
	};

	expandWorkspaceList() {
		return () => {
			this.setState({ isExpanded: true });
		};
	}

	getStoredItems(rootDomain: string) {
		const { loginRedirect } = this.state;
		return AccountStore.getAll().map(({ id, name, subdomain, logo }) => (
			<WorkspaceRow
				key={id}
				account={{ id, name, logo }}
				subdomain={subdomain}
				accountType="member"
				loginUrl={`https://${subdomain}${rootDomain}${loginRedirect ?? "/"}`}
			/>
		));
	}

	renderAccountLogo = () => {
		const { account } = window.INITIAL_STATE;

		if (account) {
			return (
				<Avatar
					circle
					link={false}
					width="7.5"
					user={{ id: "0", fullName: account.name, image: account.logo }}
					style={{ margin: "auto" }}
					className={styles.workspaceLogo}
				/>
			);
		}

		return (
			<div className={cx(styles.placeholder, styles.workspaceLogo)}>
				<FontAwesomeIcon icon={faHotel}></FontAwesomeIcon>
			</div>
		);
	};

	renderHeader() {
		const { errorCode, errorMessage } = this.state;
		const embedded = inVideocallMode || isNative || this.props.inOutlookLogin;
		const urlParams = new URLSearchParams(window.location.search);
		const nextUrl = urlParams.get("next");

		if (errorCode && errorCode === "no_account_found") {
			if (isNative) {
				return <div className={styles.noAccountError}>{errorMessage} Please sign up on web!</div>;
			}
			if (embedded) {
				const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
				const signupURL = `https://${rootDomain.slice(1)}/auth/signup/?next=${nextUrl}`;
				return (
					<div className={styles.noAccountError}>
						{errorMessage} Would you like to{" "}
						<a target="_blank" rel="noopener" href={signupURL}>
							sign up instead?
						</a>
					</div>
				);
			} else {
				const signupUrl = `/auth/signup/?next=${nextUrl}`;
				return (
					<div className={styles.noAccountError}>
						{errorMessage} Would you like to <Link to={signupUrl}>sign up instead?</Link>
					</div>
				);
			}
		}

		if (errorMessage) {
			return (
				<div data-test-id="auth-error-message" className={styles.authError}>
					{errorMessage}
				</div>
			);
		}

		if (window.ENVIRONMENT === "staging" || window.ENVIRONMENT === "sandbox") {
			return this.renderStagingAppError();
		}

		return null;
	}

	renderLoginButtons(singleColumn?: boolean) {
		const { account } = window.INITIAL_STATE;
		const provider = this.getProvider();
		let ssoLoginButton;
		if (account?.sso?.provider) {
			let onClick;
			if (account.sso.provider === "ONE_LOGIN") {
				onClick = this.loginWithOneLogin;
			} else if (account.sso.provider === "OKTA") {
				onClick = this.loginWithOkta;
			} else if (account.sso.provider === "CUSTOM") {
				onClick = this.loginWithCustomSSO;
			}

			ssoLoginButton = (
				<AuthButton onClick={onClick} provider={account.sso.provider} action="login">
					Log in with {getLoginProviderText(account.sso)}
				</AuthButton>
			);
		}

		if (account?.sso?.provider && account?.sso.is_exclusive_auth) {
			return ssoLoginButton;
		}

		const showGoogle = !provider || provider === "google" || provider === "unknown";
		const showMicrosoft = !provider || provider === "office365" || provider === "unknown";
		const numColumns = singleColumn ? 1 : showGoogle && showMicrosoft ? 2 : 1;

		return (
			<div style={{ gridTemplateColumns: `repeat(${numColumns}, 1fr)` }} className={styles.authButtons}>
				{showGoogle && (
					<AuthButton onClick={this.loginWithGoogle} provider="Google" action="login">
						Log in with Google
					</AuthButton>
				)}

				{showMicrosoft && (
					<AuthButton onClick={this.loginWithOffice365} provider="Microsoft" action="login">
						Log in with Microsoft
					</AuthButton>
				)}

				{ssoLoginButton && ssoLoginButton}
			</div>
		);
	}

	renderSignUpButton() {
		const { account } = window.INITIAL_STATE;
		const provider = this.getProvider();
		if (account?.sso?.provider && account?.sso?.is_exclusive_auth) {
			return null;
		}

		return (
			<div>
				{provider === "google" && (
					<AuthButton onClick={this.signupWithGoogle} provider="Google" action="signup">
						Sign up with Google
					</AuthButton>
				)}

				{provider === "office365" && (
					<AuthButton onClick={this.signupWithOffice365} provider="Microsoft" action="signup">
						Sign up with Microsoft
					</AuthButton>
				)}

				{(!provider || provider === "unknown") && (
					<AuthButton onClick={this.navigateTo("/auth/signup/")} action="signup">
						Sign up
					</AuthButton>
				)}
			</div>
		);
	}

	renderSubdomainScreen(account: FellowAccount, embedded: boolean, rootDomain: string, subdomain: string) {
		const { showSignup, fromEmail } = this.state;
		const domainList = listFormat(
			account.self_signup_email_domain_list.map(domain => <strong key={domain}>@{domain}</strong>),
			{ sep: ", ", last: " or " },
		);

		return (
			<>
				{this.renderAccountLogo()}
				<h2>Log in to {account.name}</h2>
				<div className={cx(styles.authSubtext, styles.loginAuthSubtext)}>
					{subdomain}
					{rootDomain}
				</div>

				{this.renderLoginButtons()}
				{account.has_guest_users && !embedded && (
					<h5 className={styles.guestLink}>
						<Link to="/auth/login/guest/">Log in with a Guest account</Link>
					</h5>
				)}

				{showSignup && !fromEmail ? (
					domainList.length ? (
						<>
							{this.renderSignUpButton()}
							<h5>You can sign up with an {domainList} email address</h5>
						</>
					) : undefined
				) : (
					<h5>
						If you have an {domainList} email address, you can <Link to="/auth/signup/">create an account</Link>
					</h5>
				)}
			</>
		);
	}

	renderRootDomainScreen(rootDomain: string, embedded: boolean) {
		const { streamInviteId, isExpanded } = this.state;
		const { invite_sender_name: inviteSenderName } = window.INITIAL_STATE;

		const storedItems = this.getStoredItems(rootDomain);

		const initialNumVisibleAccounts = 5;
		const visibleAccounts = isExpanded ? storedItems : storedItems.slice(0, initialNumVisibleAccounts);
		const extraAccounts = storedItems.length - initialNumVisibleAccounts;

		// Check if the current URL includes "redirectBackToDesktopOnLastOauthStep=1"
		const redirectBackToDesktopOnLastOauthStep = window.location.search.includes(
			"redirectBackToDesktopOnLastOauthStep=1",
		);

		// if the user is authenticating from the desktop app, showing currently logged in
		// workspaces would allow them to continue on the web, which we don't want, so we hide them
		const hideCurrentlyLoggedInWorkspaces = redirectBackToDesktopOnLastOauthStep;
		const showCurrentlyLoggedInWorkspaces = storedItems.length > 0 && !hideCurrentlyLoggedInWorkspaces;

		return (
			<>
				<div className={styles.loginButtonsContainer}>
					{streamInviteId ? (
						<>
							<h2>{inviteSenderName} wants to share notes with you for your meeting</h2>
							<h5>Choose your login method</h5>
						</>
					) : (
						<h3>Choose your login method</h3>
					)}
					{this.renderLoginButtons(!showCurrentlyLoggedInWorkspaces)}
					{!embedded && !streamInviteId && (
						<div className={styles.guestOrWorkspaceLogin}>
							<div>
								Log in to a <Link to="/auth/find/email/">Guest account</Link> or with your{" "}
								{
									// if the user is authenticating from the desktop app, we need to carry forward this query parameter
									// to the /auth/find/subdomain/ page so that we can redirect the user back to the desktop app
									// after the last OAuth step
								}
								<Link
									to={`/auth/find/subdomain/${
										redirectBackToDesktopOnLastOauthStep ? "?redirectBackToDesktopOnLastOauthStep=1" : ""
									}`}
								>
									workspace URL
								</Link>
							</div>
						</div>
					)}
				</div>

				{showCurrentlyLoggedInWorkspaces && (
					<div className={styles.loggedInWorkspacesContainer}>
						<div className={styles.loggedInWorkspacesSubheader}>Or choose a workspace you’re already logged into:</div>
						<div className={styles.loginWorkspaceButtons}>
							{visibleAccounts}
							{!this.state.isExpanded && storedItems.length > initialNumVisibleAccounts && (
								<div className={styles.workspaceListMore}>
									<div className={styles.workspaceListMoreText}>
										{extraAccounts} more workspace
										{extraAccounts > 1 ? "s" : ""}
									</div>
									<ButtonLink className={styles.showAllWorkspaces} onClick={this.expandWorkspaceList()}>
										Show all workspaces
									</ButtonLink>
								</div>
							)}
						</div>
					</div>
				)}
			</>
		);
	}

	renderStep() {
		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		const { subdomain, account } = window.INITIAL_STATE;
		const embedded = inVideocallMode || !!this.props.inOutlookLogin;

		if (inVideocallMode) {
			window.parent.postMessage(
				{
					type: "FROM_AUTH_PAGE",
					inAuthFlow: true,
				},
				"*",
			);
		}

		return (
			<div className={styles.authFlex}>
				{account && subdomain
					? this.renderSubdomainScreen(account, embedded, rootDomain, subdomain)
					: this.renderRootDomainScreen(rootDomain, embedded)}
			</div>
		);
	}

	renderFooter(): JSX.Element | null {
		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		const hasCurrentlyLoggedInWorkspaces = this.getStoredItems(rootDomain).length > 0;
		return (
			<>
				<SignupHint customText={hasCurrentlyLoggedInWorkspaces ? "Want to create another account?" : undefined} />
				<Terms />
			</>
		);
	}

	render(): JSX.Element {
		const searchParams = new URLSearchParams(window.location.search);
		if (
			searchParams.get("in_videocall") === "google" &&
			!searchParams.get("skipPreAuth") &&
			isExtensionVersionReleased("2.4.3")
		) {
			const meetingCost = parseInt(searchParams.get("meetingCost") ?? "", 10);
			return <GoogleMeetPreAuth defaultMeetingCostPerHour={meetingCost ? meetingCost : undefined} />;
		}
		return super.render();
	}
}
